import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/pro-light-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/pro-light-svg-icons/faChevronUp';
import { TypedMemo } from '../../../common/types';
import { RevisionEntry, defaultComparePair, nextRevisionNumber } from './revisionModel';
import { revisionMessages } from './revisionMessages';
import { RevisionRow } from './RevisionRow';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './Revisions.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface RevisionListProps {
  entries: RevisionEntry[];
  /** The key of the revision on the stage. */
  openKey: string;
  currentUserId: number | null;
  isNarrow: boolean;
  isDemo: boolean;
  isError: boolean;
  onOpen: (entry: RevisionEntry) => void;
  /** Opens Compare with these two revision keys (design d2). */
  onCompare: (keys: string[]) => void;
  /** "Try again as rN" of a failed revision; absent where the viewer offers no new revision. */
  onRemake?: (entry: RevisionEntry) => void;
}

/**
 * The revision history in the side panel (design d1): the newest first. Below 700 px it is a sheet
 * under the preview that the user opens with "Revisions N" (design d5), and Compare is web only.
 */
const RevisionListComponent: React.FC<RevisionListProps> = (props) => {
  const { entries, openKey, currentUserId, isNarrow, isDemo, isError, onOpen, onCompare, onRemake } = props;
  const intl = useIntl();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const toggleSheet = useCallback(() => setIsSheetOpen((open) => !open), []);
  const readyCount = useMemo(() => entries.filter((entry) => entry.state === 'ready').length, [entries]);
  const canCompare = !isNarrow && readyCount >= 2;
  const compareDefault = useCallback(() => onCompare(defaultComparePair(entries)), [entries, onCompare]);
  const compareWith = useCallback(
    (entry: RevisionEntry) => {
      const other = defaultComparePair(entries).find((key) => key !== entry.key);
      onCompare(other === undefined ? [entry.key] : [entry.key, other]);
    },
    [entries, onCompare]
  );
  const nextNumber = useMemo(() => nextRevisionNumber(entries), [entries]);

  if (isError) {
    return <span className={styles.goneNote}>{intl.formatMessage(revisionMessages.loadError)}</span>;
  }
  if (entries.length === 0) {
    return null;
  }
  const showRows = !isNarrow || isSheetOpen;

  return (
    <section className={styles.revs} aria-label={intl.formatMessage(revisionMessages.title)} data-testid="revisions">
      <div className={styles.revsHead}>
        {isNarrow ? (
          <button type="button" className={styles.revsToggle} aria-expanded={isSheetOpen} onClick={toggleSheet}>
            {intl.formatMessage(revisionMessages.titleCount, { count: entries.length })}
            <FontAwesomeIcon icon={isSheetOpen ? faChevronDown : faChevronUp} />
          </button>
        ) : (
          <span>{intl.formatMessage(revisionMessages.title)}</span>
        )}
        {canCompare && (
          <button type="button" className={styles.linkButton} onClick={compareDefault}>
            {intl.formatMessage(revisionMessages.compareTwo)}
          </button>
        )}
      </div>
      {showRows && (
        <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
          {entries.map((entry, index) => (
            <RevisionRow
              key={entry.key}
              entry={entry}
              previous={entries[index + 1]}
              isOpen={entry.key === openKey}
              currentUserId={currentUserId}
              nextNumber={nextNumber}
              canCompare={canCompare}
              isDemo={isDemo}
              onOpen={onOpen}
              onCompare={compareWith}
              onRemake={onRemake}
            />
          ))}
        </ul>
      )}
    </section>
  );
};

export const RevisionList = TypedMemo(RevisionListComponent);
